/**
 * SONAUTO — runtime translation bundles.
 *
 * A bundle is the flat, immutable, per-(tenant, branch, locale) map of
 * customer UI strings that the QR ordering PWA downloads once and caches. It
 * is built server-side from `customer_ui_translations` (published rows only)
 * and served from KV behind an ETag.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE
 * ---------------------------------------------------------------------------
 * `t()` NEVER returns a raw key to a customer. A guest must never see
 * `customer.menu.title` on their phone — that is a visible product defect and
 * it makes the restaurant look broken. On a total miss we report to telemetry
 * via `onMissing` and return the fallback-bundle text, or an empty string. An
 * empty label is ugly; a raw key is embarrassing.
 */

import { sha256Hex } from '@sonauto/utilities';

export interface TranslationBundle {
  /** BCP-47 tag this bundle is for. */
  readonly locale: string;
  /** Monotonic bundle version — bump invalidates every cached copy. */
  readonly version: number;
  /** Epoch milliseconds, server clock. */
  readonly generatedAt: number;
  /** `customer_ui_translations.namespace`, e.g. `customer`. */
  readonly namespace: string;
  /** key → translated text. Flat by design: no nesting, no arrays. */
  readonly entries: Readonly<Record<string, string>>;
}

/** Values allowed in `{placeholder}` interpolation. */
export type TranslationParams = Readonly<Record<string, string | number>>;

export interface MissingKeyEvent {
  readonly key: string;
  readonly locale: string;
  readonly namespace: string;
  /** `true` when the fallback bundle rescued the render. */
  readonly resolvedFromFallback: boolean;
}

export interface CreateTranslatorInput {
  readonly bundle: TranslationBundle;
  /**
   * Usually the vi-VN bundle. Consulted only when the primary bundle has no
   * entry, so a partially translated menu still renders.
   */
  readonly fallbackBundle?: TranslationBundle | null;
  /** Telemetry sink. Must not throw; exceptions are swallowed on purpose. */
  readonly onMissing?: (event: MissingKeyEvent) => void;
}

export interface Translator {
  /** Resolve a key. Never returns the key itself. */
  readonly t: (key: string, params?: TranslationParams) => string;
  /** True when the primary or fallback bundle can render `key`. */
  readonly hasKey: (key: string) => boolean;
  /** Which of `required` neither bundle can render. */
  readonly missingKeys: (required: readonly string[]) => readonly string[];
  readonly locale: string;
  readonly namespace: string;
}

/**
 * Matches an escaped brace (`{{` / `}}`) OR a `{name}` placeholder.
 *
 * The escapes are part of the SAME alternation, and come first, so a doubled
 * brace is consumed before the placeholder branch can see the single brace
 * inside it. Handling them in two separate passes silently ate the escaped
 * form: `{{literal}}` had its inner `{literal}` substituted as a placeholder
 * and rendered as `{}`.
 */
const PLACEHOLDER_RE = /\{\{|\}\}|\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

/**
 * Substitute `{name}` placeholders.
 *
 * A placeholder with no matching param resolves to an empty string rather than
 * leaking `{name}` to the guest — same reasoning as the raw-key rule. `{{` and
 * `}}` are literal braces.
 */
export function interpolate(template: string, params?: TranslationParams): string {
  if (template === '') return '';
  return template.replace(PLACEHOLDER_RE, (match, name: string | undefined): string => {
    if (match === '{{') return '{';
    if (match === '}}') return '}';
    if (name === undefined) return '';
    const value = params?.[name];
    if (value === undefined || value === null) return '';
    return typeof value === 'number' ? String(value) : value;
  });
}

function lookup(bundle: TranslationBundle | null | undefined, key: string): string | undefined {
  if (bundle === null || bundle === undefined) return undefined;
  if (!Object.prototype.hasOwnProperty.call(bundle.entries, key)) return undefined;
  const value = bundle.entries[key];
  if (typeof value !== 'string' || value === '') return undefined;
  return value;
}

export function createTranslator(input: CreateTranslatorInput): Translator {
  const { bundle, fallbackBundle = null, onMissing } = input;

  const report = (event: MissingKeyEvent): void => {
    if (onMissing === undefined) return;
    try {
      onMissing(event);
    } catch {
      // Telemetry must never break a customer render.
    }
  };

  const t = (key: string, params?: TranslationParams): string => {
    const primary = lookup(bundle, key);
    if (primary !== undefined) return interpolate(primary, params);

    const fallback = lookup(fallbackBundle, key);
    report({
      key,
      locale: bundle.locale,
      namespace: bundle.namespace,
      resolvedFromFallback: fallback !== undefined,
    });
    // Total miss ⇒ empty string. NEVER `key`.
    return fallback === undefined ? '' : interpolate(fallback, params);
  };

  const hasKey = (key: string): boolean =>
    lookup(bundle, key) !== undefined || lookup(fallbackBundle, key) !== undefined;

  const missingKeys = (required: readonly string[]): readonly string[] => required.filter((k) => !hasKey(k));

  return { t, hasKey, missingKeys, locale: bundle.locale, namespace: bundle.namespace };
}

// ---------------------------------------------------------------------------
// KV caching
// ---------------------------------------------------------------------------

/**
 * KV key for a cached bundle.
 *
 * `branchId` is part of the key because a branch may override tenant strings
 * (different service-charge wording, different branch name). `version` is part
 * of the key so publishing a new bundle is an atomic switch to a fresh key
 * rather than a delete-then-write race; old versions expire by TTL.
 *
 * The `i18n:v1:` prefix is the SCHEMA version of the key format itself — bump
 * it if this function's shape ever changes, to orphan (not corrupt) old keys.
 */
export function bundleCacheKey(
  tenantId: string,
  branchId: string | null | undefined,
  locale: string,
  version: number,
): string {
  const branch = branchId === null || branchId === undefined || branchId === '' ? '_' : branchId;
  return `i18n:v1:${tenantId}:${branch}:${locale}:${version}`;
}

/**
 * Content-addressed ETag over the bundle's meaningful content.
 *
 * Keys are sorted so an identical bundle always hashes identically regardless
 * of D1 row order; `generatedAt` is deliberately EXCLUDED so re-generating an
 * unchanged bundle does not bust every customer's HTTP cache.
 *
 * Weak validator (`W/`): the bytes may differ (whitespace, key order) while
 * the content is semantically identical.
 */
export async function bundleEtag(bundle: TranslationBundle): Promise<string> {
  const keys = Object.keys(bundle.entries).sort();
  const canonical = JSON.stringify({
    l: bundle.locale,
    n: bundle.namespace,
    v: bundle.version,
    e: keys.map((k) => [k, bundle.entries[k] ?? '']),
  });
  const digest = await sha256Hex(canonical);
  return `W/"${digest.slice(0, 32)}"`;
}

/** Are two bundles content-identical? Cheap pre-check before hashing. */
export function sameBundleContent(a: TranslationBundle, b: TranslationBundle): boolean {
  if (a.locale !== b.locale || a.namespace !== b.namespace) return false;
  const ak = Object.keys(a.entries);
  const bk = Object.keys(b.entries);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a.entries[k] === b.entries[k]);
}

// ---------------------------------------------------------------------------
// Validation (used by the translation-key coverage test)
// ---------------------------------------------------------------------------

export interface BundleValidationResult {
  readonly locale: string;
  readonly namespace: string;
  /** Required keys with no usable value. Non-empty ⇒ the test fails. */
  readonly missing: readonly string[];
  /** Keys present in the bundle but not in the required catalogue. */
  readonly extra: readonly string[];
  /** Keys present but blank/whitespace — a silent hole, treated as missing. */
  readonly blank: readonly string[];
  /**
   * Keys whose `{placeholder}` set differs from the reference bundle's. An AI
   * translation that drops `{minutes}` renders a sentence with a hole in it.
   */
  readonly placeholderMismatches: readonly PlaceholderMismatch[];
  readonly ok: boolean;
}

export interface PlaceholderMismatch {
  readonly key: string;
  readonly expected: readonly string[];
  readonly actual: readonly string[];
}

function placeholdersOf(template: string): readonly string[] {
  const out: string[] = [];
  const re = new RegExp(PLACEHOLDER_RE.source, 'g');
  let m: RegExpExecArray | null = re.exec(template);
  while (m !== null) {
    const name = m[1];
    if (name !== undefined && !out.includes(name)) out.push(name);
    m = re.exec(template);
  }
  return out.sort();
}

/**
 * Check a bundle against the required key catalogue (`CUSTOMER_UI_KEYS`).
 *
 * `reference` is optional and is normally the vi-VN bundle: when supplied the
 * placeholder contract is checked too.
 */
export function validateBundle(
  bundle: TranslationBundle,
  requiredKeys: readonly string[],
  reference?: TranslationBundle | null,
): BundleValidationResult {
  const required = new Set(requiredKeys);
  const missing: string[] = [];
  const blank: string[] = [];
  const placeholderMismatches: PlaceholderMismatch[] = [];

  for (const key of requiredKeys) {
    const raw = bundle.entries[key];
    if (raw === undefined) {
      missing.push(key);
      continue;
    }
    if (typeof raw !== 'string' || raw.trim() === '') {
      blank.push(key);
      continue;
    }
    const referenceText = reference?.entries[key];
    if (typeof referenceText === 'string' && referenceText !== '') {
      const expected = placeholdersOf(referenceText);
      const actual = placeholdersOf(raw);
      if (expected.join('|') !== actual.join('|')) {
        placeholderMismatches.push({ key, expected, actual });
      }
    }
  }

  const extra = Object.keys(bundle.entries).filter((k) => !required.has(k));

  return {
    locale: bundle.locale,
    namespace: bundle.namespace,
    missing,
    extra,
    blank,
    placeholderMismatches,
    ok: missing.length === 0 && blank.length === 0 && placeholderMismatches.length === 0,
  };
}

/** Build a bundle from raw D1 rows, dropping null/blank translations. */
export function buildBundle(
  locale: string,
  namespace: string,
  version: number,
  generatedAt: number,
  rows: readonly { readonly translation_key: string; readonly translated_text: string | null }[],
): TranslationBundle {
  const entries: Record<string, string> = {};
  for (const row of rows) {
    const text = row.translated_text;
    if (typeof text !== 'string' || text.trim() === '') continue;
    entries[row.translation_key] = text;
  }
  return { locale, namespace, version, generatedAt, entries };
}

/**
 * Overlay a branch bundle on top of a tenant bundle (branch wins).
 * Both must be the same locale; mismatches return the base untouched.
 */
export function mergeBundles(base: TranslationBundle, override: TranslationBundle): TranslationBundle {
  if (base.locale !== override.locale) return base;
  return {
    locale: base.locale,
    namespace: base.namespace,
    version: Math.max(base.version, override.version),
    generatedAt: Math.max(base.generatedAt, override.generatedAt),
    entries: { ...base.entries, ...override.entries },
  };
}
